import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '@/components/SEO';
import { TrendingUp, Heart, MessageSquare, BookOpen, Ticket, Smartphone, Activity, Monitor, Camera, CheckCircle, ArrowRight, Leaf, ShoppingCart, Users, Globe, Calendar, Target, BarChart3, Phone, Mail, MapPin } from 'lucide-react';
import { site } from '@/data/content';

export default function CaseStudyPumziTeas() {
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: 'How Pumzi Teas Achieved 40% Customer Retention with ZuriHub',
      description: 'Case study: Pumzi Teas, an organic tea e-commerce company founded by Rita Nagudi, achieved 40% customer retention using ZuriHub live chat, knowledge base, tickets, WhatsApp channel, live visitor analytics, heatmaps, and screen recording.',
      datePublished: '2026-09-15',
      dateModified: '2026-09-15',
      author: {
        '@type': 'Person',
        name: 'Rita Nagudi',
        jobTitle: 'CEO & Founder',
        worksFor: {
          '@type': 'Organization',
          name: 'Pumzi Teas',
          url: 'https://pumziteas.com',
        },
        image: 'https://pumziteas.com/assets/images/avatars/avatar_1_1787832637.jpeg',
      },
      publisher: {
        '@type': 'Organization',
        name: 'ZuriHub',
        logo: {
          '@type': 'ImageObject',
          url: `${site.baseUrl}/og-image.png`,
        },
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `${site.baseUrl}/case-study/pumzi-teas`,
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Pumzi Teas',
      url: 'https://pumziteas.com',
      description: 'Organic tea e-commerce company founded by Rita Nagudi, selling through website and mobile application',
      founder: {
        '@type': 'Person',
        name: 'Rita Nagudi',
        image: 'https://pumziteas.com/assets/images/avatars/avatar_1_1787832637.jpeg',
      },
      address: {
        '@type': 'PostalAddress',
        addressCountry: 'KE',
      },
      sameAs: ['https://pumziteas.com'],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Person',
      name: 'Rita Nagudi',
      jobTitle: 'CEO & Founder',
      worksFor: {
        '@type': 'Organization',
        name: 'Pumzi Teas',
        url: 'https://pumziteas.com',
      },
      image: 'https://pumziteas.com/assets/images/avatars/avatar_1_1787832637.jpeg',
      sameAs: ['https://pumziteas.com'],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What customer retention rate did Pumzi Teas achieve?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Pumzi Teas achieved a 40% customer retention rate after implementing ZuriHub comprehensive customer experience tools.',
          },
        },
        {
          '@type': 'Question',
          name: 'What ZuriHub features did Pumzi Teas use?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Pumzi Teas used live chat, knowledge base, ticket management, WhatsApp channel (independent support and marketing), live visitor (real-time website analytics), heatmaps, and screen recording to improve customer experience and retention.',
          },
        },
        {
          '@type': 'Question',
          name: 'How did heatmaps and screen recording help Pumzi Teas?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Heatmaps and screen recording helped Pumzi Teas understand customer behavior on their website, identify pain points in the shopping journey, and optimize their user experience for better conversions.',
          },
        },
      ],
    },
  ];

  useEffect(() => {
    document.title = 'Pumzi Teas Case Study: 40% Customer Retention with ZuriHub';
  }, []);

  return (
    <>
      <SEO
        title="Pumzi Teas Case Study: 40% Customer Retention with ZuriHub"
        description="Learn how Pumzi Teas, an organic tea e-commerce company, achieved 40% customer retention using ZuriHub live chat, knowledge base, tickets, WhatsApp channel, live visitor analytics, heatmaps, and screen recording."
        path="/case-study/pumzi-teas"
        jsonLd={jsonLd}
      />

      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-green-900 via-emerald-900 to-teal-900 text-white py-20 md:py-28 relative overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)'
            }}></div>
          </div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="max-w-4xl">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2 rounded-full mb-6 shadow-lg">
                <Heart className="h-4 w-4" />
                <span className="text-sm font-semibold tracking-wide uppercase">Customer Success Story</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight tracking-tight">
                How Pumzi Teas Achieved <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-green-400">40% Customer Retention</span> with ZuriHub
              </h1>
              
              <p className="text-xl md:text-2xl text-green-100 mb-8 leading-relaxed max-w-3xl">
                An organic tea e-commerce company transformed customer experience and built lasting customer relationships using comprehensive CX tools
              </p>
              
              <div className="flex flex-wrap items-center gap-4 text-sm text-green-200">
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                  <Leaf className="h-5 w-5" />
                  <span className="font-medium">Pumzi Teas</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                  <ShoppingCart className="h-5 w-5" />
                  <span className="font-medium">E-Commerce</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                  <Calendar className="h-5 w-5" />
                  <span className="font-medium">September 2026</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Key Results */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { 
                icon: 'Heart', 
                value: '40%', 
                label: 'Customer Retention',
                description: 'Repeat purchase rate',
                color: 'from-emerald-500 to-green-500'
              },
              { 
                icon: 'MessageSquare', 
                value: '85%', 
                label: 'Support Satisfaction',
                description: 'Customer CSAT score',
                color: 'from-blue-500 to-indigo-500'
              },
              { 
                icon: 'TrendingUp', 
                value: '35%', 
                label: 'Conversion Increase',
                description: 'Website conversion rate',
                color: 'from-purple-500 to-pink-500'
              },
            ].map((stat, i) => {
              const StatIcon = stat.icon === 'Heart' ? Heart : stat.icon === 'MessageSquare' ? MessageSquare : TrendingUp;
              return (
                <div key={i} className="bg-white rounded-2xl shadow-xl p-8 border border-slate-100">
                  <div className={`flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-r ${stat.color} text-white mb-4`}>
                    <StatIcon className="h-7 w-7" />
                  </div>
                  <div className="text-4xl font-extrabold text-slate-900 mb-2">{stat.value}</div>
                  <div className="text-lg font-semibold text-slate-700 mb-1">{stat.label}</div>
                  <div className="text-sm text-slate-500">{stat.description}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Article */}
            <div className="lg:col-span-2 space-y-10">
              {/* Company Overview */}
              <div className="prose prose-lg max-w-none">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <Leaf className="h-8 w-8 text-green-600" />
                  About Pumzi Teas
                </h2>
                
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-8 border border-green-200">
                  <div className="flex flex-col md:flex-row gap-8 mb-6">
                    <div className="flex-shrink-0">
                      <img
                        src="https://pumziteas.com/assets/images/avatars/avatar_1_1787832637.jpeg"
                        alt="Rita Nagudi - CEO & Founder of Pumzi Teas"
                        className="h-32 w-32 rounded-2xl object-cover shadow-lg border-4 border-white"
                      />
                    </div>
                    <div className="flex-1">
                      <p className="text-gray-700 text-lg leading-relaxed mb-4">
                        <strong>Pumzi Teas</strong> is a premium organic tea company founded by <strong>Rita Nagudi</strong>, selling high-quality, sustainably sourced teas through their e-commerce website and mobile application. The company specializes in:
                      </p>
                      <ul className="space-y-3 text-gray-700">
                        <li className="flex items-start gap-3">
                          <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                          <span><strong>Organic Tea Selection</strong> - Premium organic teas from sustainable sources</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                          <span><strong>E-Commerce Platform</strong> - User-friendly website and mobile app for tea purchases</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                          <span><strong>Subscription Service</strong> - Regular tea delivery subscriptions for loyal customers</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                          <span><strong>Tea Education</strong> - Brewing guides, tea origin stories, and customer education</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 pt-4 border-t border-green-200">
                    <a href="https://pumziteas.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-green-600 hover:text-green-700 font-semibold">
                      <Globe className="h-4 w-4" />
                      Visit Pumzi Teas Website
                      <ArrowRight className="h-4 w-4" />
                    </a>
                  </div>
                </div>
              </div>

              {/* The Challenge */}
              <div className="prose prose-lg max-w-none">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <Target className="h-8 w-8 text-red-600" />
                  The Challenge
                </h2>
                
                <p className="text-gray-700 text-lg leading-relaxed">
                  As an e-commerce business selling organic tea, Pumzi Teas faced several customer experience challenges that were impacting their customer retention and conversion rates:
                </p>

                <div className="bg-red-50 border border-red-200 rounded-2xl p-6 mt-6">
                  <h3 className="text-xl font-bold text-red-900 mb-4 flex items-center gap-2">
                    <Activity className="h-5 w-5" />
                    Key Challenges Faced
                  </h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start gap-3">
                      <span className="text-red-600 font-bold">•</span>
                      <span>Low customer retention rate due to limited post-purchase engagement</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-red-600 font-bold">•</span>
                      <span>Difficulty supporting customers across website and mobile app channels</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-red-600 font-bold">•</span>
                      <span>Lack of understanding of customer behavior on their website</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-red-600 font-bold">•</span>
                      <span>Customers unable to find answers to common tea-related questions</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-red-600 font-bold">•</span>
                      <span>Slow response times to customer inquiries affecting purchase decisions</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* The Solution */}
              <div className="prose prose-lg max-w-none">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <TrendingUp className="h-8 w-8 text-green-600" />
                  The Solution: Comprehensive ZuriHub Implementation
                </h2>
                
                <p className="text-gray-700 text-lg leading-relaxed">
                  Pumzi Teas implemented a comprehensive suite of ZuriHub features to transform their customer experience across all touchpoints. The implementation focused on understanding customer behavior, providing instant support, and building lasting relationships.
                </p>

                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-8 border border-green-200 mt-6">
                  <h3 className="text-xl font-bold text-green-900 mb-6">Features Implemented</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {[
                      { icon: 'MessageSquare', title: 'Live Chat', desc: 'Real-time customer support on website and mobile app' },
                      { icon: 'BookOpen', title: 'Knowledge Base', desc: 'Self-service tea guides, brewing instructions, and FAQs' },
                      { icon: 'Ticket', title: 'Ticket Management', desc: 'Organized tracking of customer issues and requests' },
                      { icon: 'Smartphone', title: 'WhatsApp Channel', desc: 'Independent WhatsApp support and marketing channel' },
                      { icon: 'Users', title: 'Live Visitor', desc: 'Real-time website analytics showing active visitors' },
                      { icon: 'Activity', title: 'Heatmaps', desc: 'Visual tracking of customer behavior and interaction patterns' },
                      { icon: 'Camera', title: 'Screen Recording', desc: 'Recording user sessions to understand pain points' },
                    ].map((feature, i) => {
                      const FeatureIcon = feature.icon === 'MessageSquare' ? MessageSquare : feature.icon === 'BookOpen' ? BookOpen : feature.icon === 'Ticket' ? Ticket : feature.icon === 'Smartphone' ? Smartphone : feature.icon === 'Users' ? Users : feature.icon === 'Activity' ? Activity : Camera;
                      return (
                        <div key={i} className="flex items-start gap-3 bg-white p-4 rounded-xl border border-slate-100">
                          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-100 text-green-600 flex-shrink-0">
                            <FeatureIcon className="h-5 w-5" />
                          </div>
                          <div>
                            <h4 className="font-bold text-slate-900">{feature.title}</h4>
                            <p className="text-sm text-slate-600">{feature.desc}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Live Chat Implementation */}
              <div className="prose prose-lg max-w-none">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <MessageSquare className="h-8 w-8 text-blue-600" />
                  Live Chat: Instant Support Across Channels
                </h2>
                
                <p className="text-gray-700 text-lg leading-relaxed">
                  Pumzi Teas implemented ZuriHub's live chat widget on both their website and mobile application, providing customers with instant access to support regardless of their preferred channel.
                </p>

                <div className="space-y-4 mt-6">
                  {[
                    {
                      title: 'Multi-Channel Support',
                      description: 'Live chat integration on website and mobile app with unified conversation history',
                      impact: 'Customers received consistent support across all platforms'
                    },
                    {
                      title: 'Real-Time Responses',
                      description: 'Instant support for tea-related questions, brewing guidance, and order assistance',
                      impact: 'Reduced cart abandonment by 25%'
                    },
                    {
                      title: 'Agent Efficiency',
                      description: 'Agents could handle multiple conversations simultaneously with context switching',
                      impact: 'Increased support capacity by 40%'
                    },
                    {
                      title: 'Automated Triggers',
                      description: 'Proactive chat invitations based on customer behavior and time on page',
                      impact: 'Increased engagement and conversion rates'
                    },
                  ].map((item, i) => (
                    <div key={i} className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
                      <h4 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h4>
                      <p className="text-gray-700 mb-3">{item.description}</p>
                      <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-3 py-1.5 rounded-full text-sm font-semibold">
                        <CheckCircle className="h-4 w-4" />
                        {item.impact}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Knowledge Base */}
              <div className="prose prose-lg max-w-none">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <BookOpen className="h-8 w-8 text-purple-600" />
                  Knowledge Base: Self-Service Tea Education
                </h2>
                
                <p className="text-gray-700 text-lg leading-relaxed">
                  Pumzi Teas created a comprehensive knowledge base with ZuriHub, focusing on tea education and customer self-service to reduce support volume and empower customers.
                </p>

                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-2xl p-8 border border-purple-200 mt-6">
                  <h3 className="text-xl font-bold text-purple-900 mb-6">Knowledge Base Content</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {[
                      'Tea brewing guides and water temperature recommendations',
                      'Storage instructions for different tea varieties',
                      'Health benefits and origin stories of tea types',
                      'Subscription management and delivery information',
                      'Order tracking and shipping policies',
                      'Tea pairing suggestions and recipes',
                      'Sustainability and sourcing information',
                      'FAQs about tea varieties and preparation',
                    ].map((content, i) => (
                      <div key={i} className="flex items-center gap-3 bg-white p-3 rounded-lg border border-slate-100">
                        <CheckCircle className="h-5 w-5 text-purple-600 flex-shrink-0" />
                        <span className="text-sm text-slate-700">{content}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 bg-white p-4 rounded-xl border border-purple-100">
                    <div className="flex items-center gap-3">
                      <BarChart3 className="h-6 w-6 text-purple-600" />
                      <div>
                        <h4 className="font-bold text-slate-900">Result</h4>
                        <p className="text-sm text-slate-600">Reduced support tickets by 35% with self-service options</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* WhatsApp Channel */}
              <div className="prose prose-lg max-w-none">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <Smartphone className="h-8 w-8 text-green-600" />
                  WhatsApp Channel: Independent Support & Marketing
                </h2>
                
                <p className="text-gray-700 text-lg leading-relaxed">
                  Pumzi Teas implemented WhatsApp as an independent channel for both customer support and marketing. This allowed them to reach customers where they spend most of their time while maintaining professional support operations.
                </p>

                <div className="space-y-4 mt-6">
                  {[
                    {
                      title: 'Dedicated WhatsApp Support',
                      description: 'Customers can reach support directly through WhatsApp with dedicated support agents',
                      impact: 'Higher customer satisfaction with preferred communication channel'
                    },
                    {
                      title: 'WhatsApp Marketing',
                      description: 'Send promotional messages, product updates, and special offers to opted-in customers',
                      impact: 'Increased engagement and repeat purchases'
                    },
                    {
                      title: 'Rich Media Support',
                      description: 'Share images, videos, and documents for visual tea-related support and marketing',
                      impact: 'Better understanding of customer needs and product features'
                    },
                    {
                      title: 'Automated Responses',
                      description: 'WhatsApp bots for common queries with seamless handoff to human agents',
                      impact: 'Faster response times and 24/7 availability'
                    },
                  ].map((item, i) => (
                    <div key={i} className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
                      <h4 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h4>
                      <p className="text-gray-700 mb-3">{item.description}</p>
                      <div className="inline-flex items-center gap-2 bg-green-50 text-green-700 px-3 py-1.5 rounded-full text-sm font-semibold">
                        <CheckCircle className="h-4 w-4" />
                        {item.impact}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Live Visitor */}
              <div className="prose prose-lg max-w-none">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <Users className="h-8 w-8 text-blue-600" />
                  Live Visitor: Real-Time Website Analytics
                </h2>
                
                <p className="text-gray-700 text-lg leading-relaxed">
                  Pumzi Teas implemented Live Visitor to see real-time website analytics, showing active visitors, their behavior, and enabling proactive engagement opportunities.
                </p>

                <div className="space-y-4 mt-6">
                  {[
                    {
                      title: 'Real-Time Visitor Tracking',
                      description: 'See exactly how many visitors are on the website, their pages, and time spent',
                      impact: 'Data-driven decisions for website optimization'
                    },
                    {
                      title: 'Proactive Engagement',
                      description: 'Identify high-intent visitors and initiate live chat conversations at the right moment',
                      impact: 'Increased conversion rates and customer engagement'
                    },
                    {
                      title: 'Behavioral Insights',
                      description: 'Understand visitor paths, bounce rates, and popular content in real-time',
                      impact: 'Improved user experience and content strategy'
                    },
                    {
                      title: 'Geographic Data',
                      description: 'See where visitors are coming from to tailor marketing and support strategies',
                      impact: 'Better targeting and personalized experiences'
                    },
                  ].map((item, i) => (
                    <div key={i} className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
                      <h4 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h4>
                      <p className="text-gray-700 mb-3">{item.description}</p>
                      <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-3 py-1.5 rounded-full text-sm font-semibold">
                        <CheckCircle className="h-4 w-4" />
                        {item.impact}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Heatmaps and Screen Recording */}
              <div className="prose prose-lg max-w-none">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <Activity className="h-8 w-8 text-orange-600" />
                  Heatmaps & Screen Recording: Understanding Customer Behavior
                </h2>
                
                <p className="text-gray-700 text-lg leading-relaxed">
                  Pumzi Teas leveraged ZuriHub's heatmaps and screen recording features to gain deep insights into how customers interacted with their website and mobile app, enabling data-driven optimizations.
                </p>

                <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-2xl p-8 border border-orange-200 mt-6">
                  <h3 className="text-xl font-bold text-orange-900 mb-6">Behavioral Insights & Optimizations</h3>
                  <div className="space-y-4">
                    {[
                      {
                        insight: 'High Drop-off at Product Selection',
                        action: 'Simplified tea variety selection with better filtering',
                        result: '15% increase in add-to-cart rate'
                      },
                      {
                        insight: 'Confusion About Subscription Benefits',
                        action: 'Added clear subscription comparison and FAQ section',
                        result: '30% increase in subscription sign-ups'
                      },
                      {
                        insight: 'Difficulty Finding Brewing Information',
                        action: 'Prominent brewing guides on product pages',
                        result: 'Reduced support inquiries about brewing'
                      },
                      {
                        insight: 'Mobile Navigation Issues',
                        action: 'Optimized mobile app navigation and checkout flow',
                        result: '25% improvement in mobile conversion rate'
                      },
                    ].map((item, i) => (
                      <div key={i} className="bg-white p-4 rounded-xl border border-orange-100">
                        <div className="flex items-start gap-3 mb-2">
                          <Activity className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <h4 className="font-bold text-slate-900">{item.insight}</h4>
                          </div>
                        </div>
                        <div className="ml-8 space-y-2">
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-orange-600 font-semibold">Action:</span>
                            <span className="text-slate-700">{item.action}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-green-600 font-semibold">Result:</span>
                            <span className="text-slate-700">{item.result}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* The Results */}
              <div className="prose prose-lg max-w-none">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <TrendingUp className="h-8 w-8 text-green-600" />
                  The Results
                </h2>
                
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-8 border border-green-200">
                  <h3 className="text-2xl font-bold text-green-900 mb-6">Measurable Business Impact</h3>
                  
                  <div className="space-y-6">
                    <div className="flex items-center gap-4">
                      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-green-600 text-white text-2xl font-bold">
                        40%
                      </div>
                      <div>
                        <h4 className="text-xl font-bold text-slate-900">Customer Retention Rate</h4>
                        <p className="text-gray-600">Repeat purchase rate increased significantly</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-blue-600 text-white text-2xl font-bold">
                        85%
                      </div>
                      <div>
                        <h4 className="text-xl font-bold text-slate-900">Customer Satisfaction</h4>
                        <p className="text-gray-600">CSAT score for support interactions</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-purple-600 text-white text-2xl font-bold">
                        35%
                      </div>
                      <div>
                        <h4 className="text-xl font-bold text-slate-900">Conversion Rate Increase</h4>
                        <p className="text-gray-600">Website conversion rate improvement</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-orange-600 text-white text-2xl font-bold">
                        35%
                      </div>
                      <div>
                        <h4 className="text-xl font-bold text-slate-900">Support Ticket Reduction</h4>
                        <p className="text-gray-600">Due to knowledge base self-service</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Feature Impact Summary */}
              <div className="prose prose-lg max-w-none">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Feature Impact Summary</h2>
                
                <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-lg">
                  <table className="w-full">
                    <thead className="bg-slate-900 text-white">
                      <tr>
                        <th className="px-6 py-4 text-left font-semibold">Feature</th>
                        <th className="px-6 py-4 text-left font-semibold">Implementation</th>
                        <th className="px-6 py-4 text-center font-semibold">Impact</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      <tr className="bg-slate-50">
                        <td className="px-6 py-4 font-semibold text-slate-900">Live Chat</td>
                        <td className="px-6 py-4 text-slate-700">Website & mobile app integration</td>
                        <td className="px-6 py-4 text-center text-green-600 font-bold">25% less cart abandonment</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 text-slate-700">Knowledge Base</td>
                        <td className="px-6 py-4 text-slate-700">Tea guides, brewing instructions, FAQs</td>
                        <td className="px-6 py-4 text-center text-green-600 font-bold">35% fewer support tickets</td>
                      </tr>
                      <tr className="bg-slate-50">
                        <td className="px-6 py-4 text-slate-700">Ticket Management</td>
                        <td className="px-6 py-4 text-slate-700">Organized issue tracking</td>
                        <td className="px-6 py-4 text-center text-green-600 font-bold">Faster resolution times</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 text-slate-700">WhatsApp Channel</td>
                        <td className="px-6 py-4 text-slate-700">Independent support & marketing</td>
                        <td className="px-6 py-4 text-center text-green-600 font-bold">Higher customer satisfaction</td>
                      </tr>
                      <tr className="bg-slate-50">
                        <td className="px-6 py-4 text-slate-700">Live Visitor</td>
                        <td className="px-6 py-4 text-slate-700">Real-time website analytics</td>
                        <td className="px-6 py-4 text-center text-green-600 font-bold">Proactive engagement opportunities</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 text-slate-700">Heatmaps</td>
                        <td className="px-6 py-4 text-slate-700">Behavior tracking & analysis</td>
                        <td className="px-6 py-4 text-center text-green-600 font-bold">15% higher add-to-cart</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 text-slate-700">Screen Recording</td>
                        <td className="px-6 py-4 text-slate-700">User session analysis</td>
                        <td className="px-6 py-4 text-center text-green-600 font-bold">25% better mobile conversion</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Testimonial */}
              <div className="prose prose-lg max-w-none">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">What Pumzi Teas Says</h2>
                
                <div className="bg-gradient-to-r from-green-900 to-emerald-900 rounded-2xl p-8 text-white relative overflow-hidden">
                  {/* Decorative elements */}
                  <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                  <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
                  
                  <div className="relative">
                    <div className="flex items-start gap-6">
                      <div className="flex-shrink-0">
                        <img
                          src="https://pumziteas.com/assets/images/avatars/avatar_1_1787832637.jpeg"
                          alt="Rita Nagudi - CEO & Founder of Pumzi Teas"
                          className="h-20 w-20 rounded-full object-cover border-4 border-white/30 shadow-xl"
                        />
                      </div>
                      <div className="flex-1">
                        <div className="mb-4">
                          <svg className="h-8 w-8 text-white/30" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                          </svg>
                        </div>
                        <p className="text-xl italic text-green-100 mb-6 leading-relaxed">
                          "Implementing ZuriHub transformed how we engage with our customers. The combination of live chat, knowledge base, and behavioral insights helped us understand what our customers truly need. Our retention rate jumped to 40%, and customers love the self-service options for tea education. The heatmaps and screen recording were game-changers for optimizing our user experience."
                        </p>
                        <div className="flex items-center gap-3">
                          <div>
                            <div className="font-bold text-white text-lg">Rita Nagudi</div>
                            <div className="text-green-200">CEO & Founder, Pumzi Teas</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <div className="bg-gradient-to-r from-brand-600 to-indigo-600 rounded-2xl p-8 text-white text-center">
                <h3 className="text-2xl font-bold mb-4">Ready to Achieve Similar Results?</h3>
                <p className="text-lg text-blue-100 mb-6 max-w-2xl mx-auto">
                  Join Pumzi Teas and hundreds of other businesses using ZuriHub to transform their customer experience and drive growth.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Link
                    to="/book-a-demo"
                    className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-base font-bold text-brand-600 transition hover:bg-slate-100"
                  >
                    Book a Demo
                    <ArrowRight className="h-5 w-5" />
                  </Link>
                  <Link
                    to="/pricing"
                    className="inline-flex items-center gap-2 rounded-full border-2 border-white px-8 py-4 text-base font-bold text-white transition hover:bg-white/10"
                  >
                    View Pricing
                  </Link>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-8">
              {/* Company Info Card */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <Leaf className="h-5 w-5 text-green-600" />
                  Company Details
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Globe className="h-5 w-5 text-slate-400 mt-0.5" />
                    <div>
                      <div className="text-sm text-slate-500">Website</div>
                      <a href="https://pumziteas.com" target="_blank" rel="noopener noreferrer" className="text-green-600 hover:text-green-700 font-semibold">
                        pumziteas.com
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <ShoppingCart className="h-5 w-5 text-slate-400 mt-0.5" />
                    <div>
                      <div className="text-sm text-slate-500">Industry</div>
                      <div className="text-slate-900 font-semibold">E-Commerce / Organic Tea</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Smartphone className="h-5 w-5 text-slate-400 mt-0.5" />
                    <div>
                      <div className="text-sm text-slate-500">Platforms</div>
                      <div className="text-slate-900 font-semibold">Website & Mobile App</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Leaf className="h-5 w-5 text-slate-400 mt-0.5" />
                    <div>
                      <div className="text-sm text-slate-500">Focus</div>
                      <div className="text-slate-900 font-semibold">Organic & Sustainable</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Key Metrics */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-green-600" />
                  Key Metrics
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center pb-3 border-b border-slate-100">
                    <span className="text-slate-600">Customer Retention</span>
                    <span className="font-bold text-green-600">40%</span>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b border-slate-100">
                    <span className="text-slate-600">CSAT Score</span>
                    <span className="font-bold text-green-600">85%</span>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b border-slate-100">
                    <span className="text-slate-600">Conversion Increase</span>
                    <span className="font-bold text-green-600">35%</span>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b border-slate-100">
                    <span className="text-slate-600">Ticket Reduction</span>
                    <span className="font-bold text-green-600">35%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Cart Abandonment</span>
                    <span className="font-bold text-green-600">-25%</span>
                  </div>
                </div>
              </div>

              {/* Features Used */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-green-600" />
                  Features Used
                </h3>
                <div className="space-y-3">
                  {[
                    'Live Chat',
                    'Knowledge Base',
                    'Ticket Management',
                    'WhatsApp Channel',
                    'Live Visitor',
                    'Heatmaps',
                    'Screen Recording',
                    'Mobile App Integration',
                  ].map((feature, i) => (
                    <div key={i} className="flex items-center gap-2 text-slate-700">
                      <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Related Case Studies */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <h3 className="text-xl font-bold text-slate-900 mb-4">Related Resources</h3>
                <div className="space-y-4">
                  <Link to="/case-study/codelab" className="block group">
                    <div className="flex items-center gap-3 text-slate-700 group-hover:text-green-600 transition">
                      <TrendingUp className="h-5 w-5" />
                      <span className="font-medium">CodeLab Case Study</span>
                    </div>
                  </Link>
                  <Link to="/live-chat" className="block group">
                    <div className="flex items-center gap-3 text-slate-700 group-hover:text-green-600 transition">
                      <MessageSquare className="h-5 w-5" />
                      <span className="font-medium">Live Chat Feature</span>
                    </div>
                  </Link>
                  <Link to="/knowledgebase" className="block group">
                    <div className="flex items-center gap-3 text-slate-700 group-hover:text-green-600 transition">
                      <BookOpen className="h-5 w-5" />
                      <span className="font-medium">Knowledge Base</span>
                    </div>
                  </Link>
                  <Link to="/heatmaps" className="block group">
                    <div className="flex items-center gap-3 text-slate-700 group-hover:text-green-600 transition">
                      <Activity className="h-5 w-5" />
                      <span className="font-medium">Heatmaps & Analytics</span>
                    </div>
                  </Link>
                  <Link to="/book-a-demo" className="block group">
                    <div className="flex items-center gap-3 text-slate-700 group-hover:text-green-600 transition">
                      <Calendar className="h-5 w-5" />
                      <span className="font-medium">Book a Demo</span>
                    </div>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}